import {useDispatch} from 'react-redux';
import {firstBy} from 'thenby';

import {
  ENDORSEMENT_CHANGE_TYPE,
  RISK_QUOTE_STATUS_BLOCKED,
  RISK_QUOTE_STATUS_BOUND,
  RISK_QUOTE_STATUS_PENDING,
  RISK_QUOTE_STATUS_QUOTED,
  RISK_QUOTE_STATUS_REFERRED
} from 'consts';
import {QB_RISK_QUOTES, useGetEndorsements, useGetQuotes, useGetRefreshedEndorsements} from 'lib/quoteBind';
import {showModal} from 'stores';
import * as utils from 'utils';

// app
import {RiskPolicyView} from './RiskPolicy.view';

export const RiskPolicy = ({risk, riskIsLoading}) => {
  const dispatch = useDispatch();

  const {data: quotes, isLoading} = useGetQuotes(
    QB_RISK_QUOTES,
    {id: risk?.id, refresh: false},
    {
      enabled: !!risk?.id,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const {
    data: endorsements,
    isLoading: isEndorsementLoading,
  } = useGetEndorsements(risk?.id, {
    enabled: !!risk?.id,
  });

  const { mutateAsync: refreshEndorsements, isLoading: isRefreshingEndorsements } = useGetRefreshedEndorsements();

  const effectiveEndorsements = endorsements?.filter((endorsement) => endorsement.status === RISK_QUOTE_STATUS_BOUND) || [];
  const effectiveEndorsementsSorted =
    utils.sort
      .arrayNestedPropertyValue(effectiveEndorsements, 'response.responseDate', 'asc')
      ?.map((endorsement, index) => ({
        ...endorsement,
        index: index + 1,
      }))
      .sort(firstBy(utils.sort.array('date', 'effectiveFrom', 'asc'))) || [];

  const pendingEndorsements = endorsements?.filter((endorsement) => endorsement.status === RISK_QUOTE_STATUS_PENDING) || [];
  const quotedEndorsements =
    endorsements?.filter((endorsement) => ![RISK_QUOTE_STATUS_PENDING, RISK_QUOTE_STATUS_BOUND].includes(endorsement.status)) || [];

  const activeEndorsements =
    endorsements?.filter((endorsement) => [RISK_QUOTE_STATUS_QUOTED, RISK_QUOTE_STATUS_REFERRED, RISK_QUOTE_STATUS_BLOCKED].includes(endorsement.status) && endorsement.changeType === ENDORSEMENT_CHANGE_TYPE.DEFAULT) || [];

  const cancellationEndorsement =
    [...effectiveEndorsements, ...pendingEndorsements].find(
      (endorsement) => endorsement.changeType === ENDORSEMENT_CHANGE_TYPE.CANCELLATION
    ) || null;

  const extensionEndorsement =
    [...effectiveEndorsements, ...pendingEndorsements].find(
      (endorsement) => endorsement.changeType === ENDORSEMENT_CHANGE_TYPE.EXTENSION
    ) || null;

  const isCanceled = Boolean(cancellationEndorsement);
  const isExtended = Boolean(extensionEndorsement);

  const latestEffectiveEndorsementDate = cancellationEndorsement?.effectiveFrom || null;

  const hasBoundQuote =
    utils.generic.isValidArray(quotes, true) && quotes?.some((q) => q.response && q.response.responseStatus === RISK_QUOTE_STATUS_BOUND);

  const policy = quotes?.find((q) => q.response && q.response.responseStatus === RISK_QUOTE_STATUS_BOUND) || null;
  const policyCurrency = policy?.currency || null;
  const hasSurplusLineTax = risk?.hasSurplusLineTax ?? false;
  const hasBackdateCancellation = Boolean(risk?.hasCancellationBackdate);
  const hasBackdateEndorsement = Boolean(risk?.hasEndorsementBackdate);
  const hasEndorsementAdditionalPremium = Boolean(risk?.hasEndorsementAdditionalPremium);
  const hasRestrictedEndorsementForProducer = Boolean(risk?.hasRestrictedEndorsementForProducer);

  const executePreBind = (quote, isPreBind, isReqBind) => {
    const product = risk.riskType;
    const modalTitle = isPreBind ? `${utils.string.t('risks.preBind')}` : `${utils.string.t('risks.postBind')}`;

    dispatch(
      showModal({
        component: 'PRE_BIND_QUOTE',
        props: {
          title: modalTitle,
          fullWidth: true,
          disableBackdropClick: true,
          enableFullScreen: true,
          maxWidth: 'lg',
          componentProps: {
            product,
            risk,
            quote,
            isPreBind,
            isReqBind,
          },
        },
      })
    );
  };

  const handlePreBind = (quote, isPreBind) => (event) => {
    executePreBind(quote, isPreBind, false);
  };

  const handleAddEndorsement = (endorsementRisk, effectiveDate) => {
    const {riskType, id: riskId, risk: riskData} = {...endorsementRisk};
    const effectiveDateText = utils.string.t('format.date', {
      value: {date: effectiveDate, format: 'll', default: '-'},
    });
    const modalTitle = `${utils.string.t('app.addEndorsement')} - ${effectiveDateText} - ${endorsementRisk?.insured?.name}`;

    dispatch(
      showModal({
        component: 'ADD_EDIT_QUOTE_BIND',
        props: {
          title: modalTitle,
          fullWidth: true,
          disableBackdropClick: true,
          enableFullScreen: true,
          maxWidth: 'xl',
          componentProps: {
            product: {value: riskType},
            riskData,
            riskId,
            addEndorsement: true,
            effectiveDate,
          },
        },
      })
    );
  };

  const handleReQuoteEndorsement = (endorsementId, effectiveDate) => {
    const {riskType, id: riskId} = risk;
    const endorsementData = endorsements?.find((item) => item.id === endorsementId);
    const modalSubtitle = utils.string.t('app.requoteEndorsement');
    const effectiveDateText = utils.string.t('format.date', {
      value: {date: effectiveDate, format: 'll', default: '-'},
    });
    const modalTitle = `${modalSubtitle} - ${effectiveDateText} - ${risk?.insured?.name}`;

    dispatch(
      showModal({
        component: 'ADD_EDIT_QUOTE_BIND',
        props: {
          title: modalTitle,
          fullWidth: true,
          disableBackdropClick: true,
          enableFullScreen: true,
          maxWidth: 'xl',
          componentProps: {
            product: {value: riskType},
            riskData: {...endorsementData?.postPatchRisk, comments: endorsementData?.comments},
            riskId,
            addEndorsement: true,
            effectiveDate,
            endorsementId,
          },
        },
      })
    );
  };

  const handleEndorsementRefresh = async () => {
    const riskId = risk?.id;
    if (!riskId) return;

    await refreshEndorsements({ riskId });
  };

  return (
    <RiskPolicyView
      isEndorsementLoading={isEndorsementLoading || isRefreshingEndorsements}
      isLoading={isLoading}
      riskIsLoading={riskIsLoading}
      policy={policy}
      risk={risk}
      isCanceled={isCanceled}
      isExtended={isExtended}
      policyCurrency={policyCurrency}
      effectiveEndorsements={effectiveEndorsementsSorted}
      pendingEndorsements={pendingEndorsements}
      quotedEndorsements={quotedEndorsements}
      activeEndorsements={activeEndorsements}
      latestEffectiveEndorsementDate={latestEffectiveEndorsementDate}
      handleEndorsementRefresh={handleEndorsementRefresh}
      hasBoundQuote={hasBoundQuote}
      hasBackdateCancellation={hasBackdateCancellation}
      hasBackdateEndorsement={hasBackdateEndorsement}
      hasSurplusLineTax={hasSurplusLineTax}
      hasEndorsementAdditionalPremium={hasEndorsementAdditionalPremium}
      hasRestrictedEndorsementForProducer={hasRestrictedEndorsementForProducer}
      handlePreBind={handlePreBind}
      handleAddEndorsement={handleAddEndorsement}
      handleReQuoteEndorsement={handleReQuoteEndorsement}
    />
  );
};
